/*
 * DataRapor iframe SDK and parent-side broker.
 *
 * Imported HTML pages never receive the browser JWT. They request an allowed
 * operation through postMessage and the parent dashboard performs the API call
 * with its existing authenticated session.
 */
(function initDataRaporEmbed(root, factory) {
  const core = factory()

  if (typeof module === 'object' && module.exports) module.exports = core
  if (root) root.DataRaporEmbed = Object.freeze({ ...core, ...core.createBrowserApi(root) })
})(typeof globalThis !== 'undefined' ? globalThis : this, function createDataRaporEmbedCore() {
  'use strict'

  const CHANNEL = 'datarapor.embed.v1'
  const MESSAGE_TYPES = Object.freeze({
    READY: 'ready',
    REQUEST: 'request',
    RESULT: 'result'
  })
  const OPERATIONS = Object.freeze({ HISTORY: 'history' })
  const ALLOWED_FREQUENCIES = new Map([
    ['minute', 'Minute'],
    ['hour', 'Hour'],
    ['day', 'Day']
  ])
  const MAX_TAGS = 50
  const MAX_CONCURRENT_REQUESTS = 2
  const DEFAULT_TIMEOUT_MS = 130000

  function normalizeOrigin(value, base) {
    const raw = String(value || '').trim()
    if (!raw) return ''
    try {
      const url = new URL(raw, base || undefined)
      return ['http:', 'https:'].includes(url.protocol) ? url.origin : ''
    } catch (_) {
      return ''
    }
  }

  function trustedOrigins(globalScope, extraOrigins = []) {
    const origins = new Set()
    const locationOrigin = normalizeOrigin(globalScope && globalScope.location && globalScope.location.origin)
    if (locationOrigin) origins.add(locationOrigin)

    const cdnOrigin = normalizeOrigin(
      globalScope && globalScope.DataRaporConfig && globalScope.DataRaporConfig.cdnBaseUrl,
      locationOrigin || undefined
    )
    if (cdnOrigin) origins.add(cdnOrigin)

    for (const value of extraOrigins || []) {
      const origin = normalizeOrigin(value, locationOrigin || undefined)
      if (origin) origins.add(origin)
    }
    return origins
  }

  function parentOrigin(globalScope, explicitOrigin) {
    const explicit = normalizeOrigin(explicitOrigin)
    if (explicit) return explicit

    const ancestorOrigins = globalScope && globalScope.location && globalScope.location.ancestorOrigins
    const ancestor = ancestorOrigins && ancestorOrigins.length ? normalizeOrigin(ancestorOrigins[0]) : ''
    if (ancestor) return ancestor

    const referrer = globalScope && globalScope.document && globalScope.document.referrer
    const referrerOrigin = normalizeOrigin(referrer)
    if (referrerOrigin) return referrerOrigin

    return normalizeOrigin(globalScope && globalScope.location && globalScope.location.origin)
  }

  function normalizeTags(value) {
    const source = Array.isArray(value) ? value : String(value || '').split(',')
    const tags = Array.from(new Set(source.map(tag => String(tag || '').trim()).filter(Boolean)))
    if (!tags.length) throw new Error('Geçmiş veri sorgusu için en az bir tag zorunludur.')
    if (tags.length > MAX_TAGS) throw new Error(`Bir sorguda en fazla ${MAX_TAGS} tag kullanılabilir.`)
    if (tags.some(tag => tag.length > 200 || tag.includes(','))) throw new Error('Geçersiz tag adı.')
    return tags
  }

  function requiredText(value, label) {
    const text = String(value || '').trim()
    if (!text) throw new Error(`${label} zorunludur.`)
    if (text.length > 80) throw new Error(`${label} geçersiz.`)
    return text
  }

  function normalizeHistoryRequest(input = {}) {
    const tags = normalizeTags(input.tags || input.tagNameArray)
    const fromDate = requiredText(input.from || input.fromDate, 'Başlangıç tarihi')
    const toDate = requiredText(input.to || input.toDate, 'Bitiş tarihi')
    const frequencyKey = String(input.frequency || input.freq || 'Hour').trim().toLowerCase()
    const freq = ALLOWED_FREQUENCIES.get(frequencyKey)
    if (!freq) throw new Error('Frekans Minute, Hour veya Day olmalıdır.')

    return {
      tagNameArray: tags.join(','),
      fromDate,
      toDate,
      freq
    }
  }

  function requestId(globalScope) {
    const cryptoApi = globalScope && globalScope.crypto
    if (cryptoApi && typeof cryptoApi.randomUUID === 'function') return cryptoApi.randomUUID()
    return `embed-${Date.now()}-${Math.random().toString(36).slice(2)}`
  }

  async function parseResponse(response) {
    const text = await response.text()
    if (!text) return null
    try { return JSON.parse(text) } catch (_) { return { message: text } }
  }

  function responseError(status, payload) {
    return String(
      payload && (payload.message || payload.detail || payload.error)
      || `Geçmiş veri isteği başarısız oldu (HTTP ${status}).`
    )
  }

  function defaultAuthenticatedRequest(globalScope, url, options) {
    if (!globalScope || !globalScope.Auth || typeof globalScope.Auth.authenticatedFetch !== 'function') {
      throw new Error('Ana sayfadaki güvenli oturum servisi kullanılamıyor.')
    }
    return globalScope.Auth.authenticatedFetch(url, options)
  }

  class HostController {
    constructor(options = {}) {
      this.globalScope = options.globalScope || (typeof globalThis !== 'undefined' ? globalThis : null)
      this.request = options.request || ((url, requestOptions) => defaultAuthenticatedRequest(this.globalScope, url, requestOptions))
      this.allowedOrigins = trustedOrigins(this.globalScope, options.allowedOrigins)
      this.entries = new Set()
      this.messageHandler = event => this.handleMessage(event)
      if (this.globalScope && typeof this.globalScope.addEventListener === 'function') {
        this.globalScope.addEventListener('message', this.messageHandler)
      }
    }

    iframeOrigin(iframe, source) {
      const base = this.globalScope && this.globalScope.location && this.globalScope.location.href
      const raw = String(
        source
        || (iframe && typeof iframe.getAttribute === 'function' && iframe.getAttribute('src'))
        || (iframe && iframe.src)
        || ''
      ).trim()
      return normalizeOrigin(raw, base)
    }

    attachIframe(iframe, options = {}) {
      if (!iframe) return false
      const origin = this.iframeOrigin(iframe, options.source)
      if (!origin || !this.allowedOrigins.has(origin)) return false

      for (const entry of this.entries) {
        if (entry.iframe === iframe) {
          entry.origin = origin
          return true
        }
      }

      const entry = { iframe, origin, inFlight: 0 }
      this.entries.add(entry)
      if (iframe.dataset) iframe.dataset.dataRaporEmbed = OPERATIONS.HISTORY
      if (typeof iframe.addEventListener === 'function') {
        iframe.addEventListener('load', () => {
          this.post(entry, {
            channel: CHANNEL,
            type: MESSAGE_TYPES.READY,
            capabilities: [OPERATIONS.HISTORY]
          })
        })
      }
      return true
    }

    entryFor(event) {
      for (const entry of Array.from(this.entries)) {
        if (!entry.iframe || entry.iframe.isConnected === false) {
          this.entries.delete(entry)
          continue
        }
        if (entry.iframe.contentWindow === event.source && entry.origin === event.origin) return entry
      }
      return null
    }

    post(entry, message) {
      if (!entry || !entry.iframe || !entry.iframe.contentWindow) return
      entry.iframe.contentWindow.postMessage(message, entry.origin)
    }

    async execute(operation, payload) {
      if (operation !== OPERATIONS.HISTORY) throw new Error('Bu iframe işlemi desteklenmiyor.')
      const requestPayload = normalizeHistoryRequest(payload)
      const response = await this.request('/api/v1/chart/multi-tags-data', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(requestPayload)
      })
      const result = await parseResponse(response)
      if (!response.ok) throw new Error(responseError(response.status, result))
      return result
    }

    async handleMessage(event) {
      const entry = this.entryFor(event)
      const message = event && event.data
      if (!entry || !message || typeof message !== 'object') return
      if (message.channel !== CHANNEL || message.type !== MESSAGE_TYPES.REQUEST) return

      const id = String(message.request_id || '').trim().slice(0, 200)
      if (!id) return
      const response = {
        channel: CHANNEL,
        type: MESSAGE_TYPES.RESULT,
        request_id: id,
        operation: String(message.operation || ''),
        ok: true
      }

      if (entry.inFlight >= MAX_CONCURRENT_REQUESTS) {
        response.ok = false
        response.error = 'Bu iframe için aynı anda en fazla iki istek çalıştırılabilir.'
        this.post(entry, response)
        return
      }

      entry.inFlight += 1
      try {
        response.data = await this.execute(response.operation, message.payload)
      } catch (error) {
        response.ok = false
        response.error = error && error.message ? error.message : 'Iframe veri isteği başarısız oldu.'
      } finally {
        entry.inFlight -= 1
      }
      this.post(entry, response)
    }

    destroy() {
      if (this.globalScope && typeof this.globalScope.removeEventListener === 'function') {
        this.globalScope.removeEventListener('message', this.messageHandler)
      }
      this.entries.clear()
    }
  }

  class Client {
    constructor(options = {}) {
      this.globalScope = options.globalScope || (typeof globalThis !== 'undefined' ? globalThis : null)
      this.parentWindow = options.parentWindow || (this.globalScope && this.globalScope.parent)
      this.parentOrigin = parentOrigin(this.globalScope, options.parentOrigin)
      this.timeoutMs = Number.isFinite(options.timeoutMs) ? options.timeoutMs : DEFAULT_TIMEOUT_MS
      this.pending = new Map()
      this.messageHandler = event => this.handleMessage(event)
      if (this.globalScope && typeof this.globalScope.addEventListener === 'function') {
        this.globalScope.addEventListener('message', this.messageHandler)
      }
    }

    handleMessage(event) {
      if (!event || event.source !== this.parentWindow || event.origin !== this.parentOrigin) return
      const message = event.data
      if (!message || message.channel !== CHANNEL || message.type !== MESSAGE_TYPES.RESULT) return
      const pending = this.pending.get(String(message.request_id || ''))
      if (!pending) return
      this.pending.delete(String(message.request_id))
      clearTimeout(pending.timer)
      if (message.ok) pending.resolve(message.data)
      else pending.reject(new Error(message.error || 'DataRapor iframe isteği başarısız oldu.'))
    }

    request(operation, payload) {
      if (!this.parentWindow || this.parentWindow === this.globalScope || !this.parentOrigin) {
        return Promise.reject(new Error('DataRapor ana sayfa bağlantısı bulunamadı.'))
      }
      const id = requestId(this.globalScope)
      return new Promise((resolve, reject) => {
        const timer = setTimeout(() => {
          this.pending.delete(id)
          reject(new Error('DataRapor iframe isteği zaman aşımına uğradı.'))
        }, this.timeoutMs)
        this.pending.set(id, { resolve, reject, timer })
        this.parentWindow.postMessage({
          channel: CHANNEL,
          type: MESSAGE_TYPES.REQUEST,
          request_id: id,
          operation,
          payload
        }, this.parentOrigin)
      })
    }

    history(payload) {
      return this.request(OPERATIONS.HISTORY, payload)
    }

    destroy() {
      if (this.globalScope && typeof this.globalScope.removeEventListener === 'function') {
        this.globalScope.removeEventListener('message', this.messageHandler)
      }
      for (const pending of this.pending.values()) {
        clearTimeout(pending.timer)
        pending.reject(new Error('DataRapor iframe istemcisi kapatıldı.'))
      }
      this.pending.clear()
    }
  }

  function createBrowserApi(globalScope) {
    let client
    const getClient = () => {
      if (!client) client = new Client({ globalScope })
      return client
    }
    return {
      createClient: options => new Client({ ...options, globalScope: options && options.globalScope || globalScope }),
      history: payload => getClient().history(payload)
    }
  }

  return Object.freeze({
    CHANNEL,
    MESSAGE_TYPES,
    OPERATIONS,
    HostController,
    Client,
    createBrowserApi,
    normalizeHistoryRequest,
    normalizeOrigin,
    parentOrigin,
    trustedOrigins
  })
})
